import React, { useEffect, useRef } from 'react';

const AdvancedBackground = ({ darkMode = false }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;

    // Ajustar tamaño del canvas
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Configuración
    const gridSize = 50;
    const gridRows = Math.ceil(canvas.height / gridSize) + 2;
    const gridCols = Math.ceil(canvas.width / gridSize) + 2;
    
    let time = 0;
    let mouseX = canvas.width / 2;
    let mouseY = canvas.height / 2;

    // Seguimiento del mouse
    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseX = e.clientX - rect.left;
      mouseY = e.clientY - rect.top;
    };

    canvas.addEventListener('mousemove', handleMouseMove);

    // Partículas interactivas
    class Particle {
      constructor() {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.radius = Math.random() * 2 + 1;
        this.opacity = Math.random() * 0.5 + 0.3;
      }

      update() {
        this.x += this.vx;
        this.y += this.vy;

        // Rebote en los bordes
        if (this.x < 0 || this.x > canvas.width) this.vx *= -1;
        if (this.y < 0 || this.y > canvas.height) this.vy *= -1;

        // Interacción con el mouse
        const dx = mouseX - this.x;
        const dy = mouseY - this.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < 150) {
          const force = (150 - distance) / 150;
          this.vx -= (dx / distance) * force * 0.02;
          this.vy -= (dy / distance) * force * 0.02;
        }
      }

      draw() {
        ctx.save();
        ctx.globalAlpha = this.opacity;
        const gradient = ctx.createRadialGradient(this.x, this.y, 0, this.x, this.y, this.radius * 3);
        gradient.addColorStop(0, darkMode ? 'rgba(147, 197, 253, 0.8)' : 'rgba(99, 102, 241, 0.8)');
        gradient.addColorStop(1, 'transparent');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius * 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
    }

    const particles = Array.from({ length: window.innerWidth < 768 ? 30 : 60 }, () => new Particle());

    // Función de animación principal
    const animate = () => {
      time += 0.01;
      
      // Fondo con gradiente animado
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      const hue1 = (time * 10) % 360;
      const hue2 = (time * 10 + 60) % 360;
      gradient.addColorStop(0, darkMode 
        ? `hsla(${hue1}, 30%, 10%, 0.3)` 
        : `hsla(${hue1}, 40%, 95%, 0.2)`);
      gradient.addColorStop(1, darkMode 
        ? `hsla(${hue2}, 30%, 10%, 0.3)` 
        : `hsla(${hue2}, 40%, 95%, 0.2)`);
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Grid 3D animado
      ctx.strokeStyle = darkMode ? 'rgba(147, 197, 253, 0.15)' : 'rgba(99, 102, 241, 0.12)';
      ctx.lineWidth = 0.5;

      const offsetX = canvas.width / 2;
      const offsetY = canvas.height / 2;
      const depth = 200;

      for (let i = 0; i < gridRows; i++) {
        for (let j = 0; j < gridCols; j++) {
          const x = (j - gridCols / 2) * gridSize;
          const y = (i - gridRows / 2) * gridSize;
          
          // Efecto 3D con perspectiva
          const z = Math.sin(time + x * 0.01) * 50 + Math.cos(time + y * 0.01) * 50;
          const scale = depth / (depth + z);
          
          const px = offsetX + x * scale;
          const py = offsetY + y * scale;
          
          // Dibujar líneas horizontales
          if (j < gridCols - 1) {
            const nextX = (j + 1 - gridCols / 2) * gridSize;
            const nextZ = Math.sin(time + nextX * 0.01) * 50 + Math.cos(time + y * 0.01) * 50;
            const nextScale = depth / (depth + nextZ);
            const nextPx = offsetX + nextX * nextScale;
            
            ctx.beginPath();
            ctx.moveTo(px, py);
            ctx.lineTo(nextPx, py);
            ctx.stroke();
          }
          
          // Dibujar líneas verticales
          if (i < gridRows - 1) {
            const nextY = (i + 1 - gridRows / 2) * gridSize;
            const nextZ = Math.sin(time + x * 0.01) * 50 + Math.cos(time + nextY * 0.01) * 50;
            const nextScale = depth / (depth + nextZ);
            const nextPy = offsetY + nextY * nextScale;
            
            ctx.beginPath();
            ctx.moveTo(px, py);
            ctx.lineTo(px, nextPy);
            ctx.stroke();
          }
        }
      }

      // Actualizar y dibujar partículas
      particles.forEach(particle => {
        particle.update();
        particle.draw();
      });

      // Conexiones entre partículas (red neuronal)
      particles.forEach((particle, i) => {
        particles.slice(i + 1).forEach(otherParticle => {
          const dx = particle.x - otherParticle.x;
          const dy = particle.y - otherParticle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < 150) {
            ctx.save();
            const opacity = (1 - distance / 150) * 0.3;
            ctx.globalAlpha = opacity;
            ctx.strokeStyle = darkMode ? 'rgba(147, 197, 253, 0.6)' : 'rgba(99, 102, 241, 0.5)';
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(otherParticle.x, otherParticle.y);
            ctx.stroke();
            ctx.restore();
          }
        });
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    // Verificar si el usuario prefiere movimiento reducido
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    if (!prefersReducedMotion) {
      animate();
    }

    // Cleanup
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      canvas.removeEventListener('mousemove', handleMouseMove);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [darkMode]);

  return (
    <canvas
      ref={canvasRef}
      className="advanced-background"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0
      }}
    />
  );
};

export default AdvancedBackground;
